import { useCallback } from 'react';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { scrollToElement } from '@confluence/scroll/entry-points/scrollToElement.ts';

import {
	CONNECTION_ROW_ACTION_SUBJECT_ID,
	SCROLL_RESULT_ACTION_SUBJECT_ID,
	type SmartLinkScrollResult,
} from '../analytics.ts';
import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';
import {
	findRenderedSmartLinkElement,
	getSmartLinkContentRoot,
} from '../utils/findRenderedSmartLinkElement.ts';
import type { ConnectionRowInteractionHandler } from '../utils/connectionRowActionTypes.ts';
import type { EnhancedSmartLink, LinkDirection } from '../utils/smartLinkTypes.ts';

export type UseConnectionRowNavigationOptions = {
	item: EnhancedSmartLink;
	linkDirection: LinkDirection;
	analyticsSource: string;
	onInteraction?: ConnectionRowInteractionHandler;
	onNavigate?: () => void;
};

export const useConnectionRowNavigation = ({
	item,
	linkDirection,
	analyticsSource,
	onInteraction,
	onNavigate,
}: UseConnectionRowNavigationOptions): (() => void) => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();

	const fireItemClickAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'connectionRow',
				actionSubjectId: CONNECTION_ROW_ACTION_SUBJECT_ID,
				source: analyticsSource,
				attributes: {
					product: item.product,
					resourceType: item.resourceType,
					access: item.access,
					occurrenceIndex: item.occurrenceIndex,
					isLivePage,
				},
			},
		}).fire();
	}, [analyticsSource, createAnalyticsEvent, isLivePage, item]);

	const fireScrollResultAnalytics = useCallback(
		(result: SmartLinkScrollResult) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: result === 'success' ? 'succeeded' : 'failed',
					actionSubject: 'smartLinkScroll',
					actionSubjectId: SCROLL_RESULT_ACTION_SUBJECT_ID,
					source: analyticsSource,
					attributes: {
						result,
						reason: result === 'success' ? undefined : result,
						product: item.product,
						resourceType: item.resourceType,
						occurrenceIndex: item.occurrenceIndex,
						isLivePage,
					},
				},
			}).fire();
		},
		[analyticsSource, createAnalyticsEvent, isLivePage, item],
	);

	const handleRowClick = useCallback(() => {
		fireItemClickAnalytics();
		onInteraction?.(item, 'click');
		if (linkDirection === 'incoming') {
			window.open(item.url, '_blank', 'noopener,noreferrer');
			onNavigate?.();
			return;
		}

		const root = getSmartLinkContentRoot(isLivePage);
		if (!root) {
			fireScrollResultAnalytics('not_found');
			return;
		}

		const result = findRenderedSmartLinkElement({
			root,
			url: item.url,
		});

		if (result.status !== 'found') {
			fireScrollResultAnalytics(result.status);
			return;
		}

		try {
			scrollToElement(result.element, undefined, 'smooth', isLivePage);
			fireScrollResultAnalytics('success');
			onNavigate?.();
		} catch {
			fireScrollResultAnalytics('scroll_error');
		}
	}, [
		fireItemClickAnalytics,
		fireScrollResultAnalytics,
		isLivePage,
		item,
		linkDirection,
		onInteraction,
		onNavigate,
	]);

	return handleRowClick;
};
